import { FC, useCallback, useState, useMemo, RefObject, createContext } from 'react'
import { isShrunkSidebarUrl } from 'helpers/pageHelpers'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { isMobileOrTabletSelector } from 'selectors/viewportSelectors'
import { NotificationTrayRenderLevel } from 'interfaces/component'
import { shallowEqual } from 'tangle-shared'

export const NotificationTrayContext = createContext<NotificationTrayContextType>({
  notificationTrayRenderLevel: 'app',
  buttonProps: null,
  buttonRootRef: null,
  onButtonRootRef: () => { /* do nothing */ },
  onNextButtonProps: () => { /* do nothing */ },
})

type NotificationTrayContextProviderProps = {
  url: string
}

export type NotificationTrayContextType = {
  notificationTrayRenderLevel: NotificationTrayRenderLevel
  // tray → button to render the button according to the tray's props
  buttonProps: NotificationButtonContextProps | null
  // button → tray to calculate outer click for tray
  buttonRootRef: RefObject<HTMLButtonElement | null> | null,
  onButtonRootRef: (ref: RefObject<HTMLButtonElement | null>) => void
  onNextButtonProps: (props: NotificationButtonContextProps | null) => void
}

export type NotificationButtonContextProps = {
  isExpanded: boolean
  counter: number
  onClick: () => void
}

/*
  the reason two levels of rendering were introduced because
  fixed position can't work normally on pages with collapsed sidebat pages because App turns css-transform on there

  this context also binds <NotificationTrayButton /> and <NotificationTray /> because those are moved apart of each other in the React tree
*/
export const NotificationTrayContextProvider: FC<NotificationTrayContextProviderProps> = (props) => {
  const { url, children } = props
  const [buttonProps, setButtonProps] = useState<NotificationButtonContextProps | null>(null)
  const [buttonRef, setButtonRef] = useState<RefObject<HTMLButtonElement> | null>(null)

  const isAuthenticated = Boolean(useStoreSelector(isAuthenticatedSelector))
  const isTablet = useStoreSelector(isMobileOrTabletSelector)

  const toRenderAtAppLevel = isShrunkSidebarUrl(url, isAuthenticated) && !isTablet
  const renderLevel: NotificationTrayRenderLevel = toRenderAtAppLevel ? 'app' : 'page'

  const handleNextButtonProps = useCallback<(props: NotificationButtonContextProps | null) => void>((nextProps) => {
    if (!shallowEqual(buttonProps, nextProps)) {
      setButtonProps(nextProps)
    }

  }, [setButtonProps, buttonProps])

  const handleNextButtonRef = useCallback<(ref: RefObject<HTMLButtonElement>) => void>((ref) => {
    if (buttonRef === ref && buttonRef && buttonRef.current === ref.current) {
      return
    }
    setButtonRef(ref)
  }, [setButtonRef, buttonRef])

  const contextValue = useMemo<NotificationTrayContextType>(() => ({
    notificationTrayRenderLevel: renderLevel,
    buttonProps,
    buttonRootRef: buttonRef,
    onNextButtonProps: handleNextButtonProps,
    onButtonRootRef: handleNextButtonRef,
  }), [renderLevel, buttonProps, buttonRef, handleNextButtonProps, handleNextButtonRef])

  return (
    <NotificationTrayContext.Provider value={ contextValue }>
      { children }
    </NotificationTrayContext.Provider>
  )
}

export default NotificationTrayContextProvider
